/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Ryan Duffy
 */

import { Injectable } from '@angular/core';
import { ProjectModel } from '@app/models/project.model';
import { ApiBaseService } from '@app/services/api-services/api-base.service';
import { HttpClient } from '@angular/common/http';
import { AuthUserService } from '@app/services/authentication/auth-user-service';
@Injectable({
  providedIn: 'root',
})
export class ProjectsListService extends ApiBaseService {
  private _projectsList: ProjectModel[] = [];
  protected _endpoint = 'projects';

  constructor(http: HttpClient, authUserService: AuthUserService) {
    super(http, authUserService);
  }

  get projectsList(): ProjectModel[] {
    return this._projectsList;
  }

  set projectsList(value: ProjectModel[]) {
    this._projectsList = value;
  }

  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  getProjectsById
   *
   * : Get an Project based on the id by checking the list
   *
   *
   * @returns Void: gets the project-analytics with the id that meets the query
   * @param projectId
   */
  getProjectsById(projectId: string): ProjectModel {
    for (const project of this.projectsList) {
      if (project.projectId === projectId) {
        return project;
      }
    }
    return null;
  }

  /**
   *  Warren Dowey
   *
   *  getProjectBySlug
   *
   * : Get an Project based on the slug by checking the list
   *
   * @param slug of type string
   *
   * @returns a ProjectModel or Null
   */
  getProjectBySlug(slug: string): ProjectModel {
    for (const project of this.projectsList) {
      if (project.projectSlug === slug) {
        return project;
      }
    }
    return null;
  }

  /**
   *  Ryan Duffy
   *
   *  getProjectBySlug
   *
   * : Get an Project based on the slug by checking the list
   *
   * @param slug of type string
   *
   * @returns a ProjectModel[] or []
   */
  getProjectsBySlug(slug: string): ProjectModel[] {
    const projectArray: ProjectModel[] = [];
    for (const project of this.projectsList) {
      if (project.projectSlug === slug) {
        projectArray.push(project);
      }
    }
    return projectArray;
  }

  /**
   *  Ryan Duffy
   *
   *  getProjectsByOrganisationId
   *
   * : Get an Project based on the organisationId by checking the list
   *
   * @param organisationId of type string
   *
   * @returns a ProjectModel[] or []
   */
  getProjectsByOrganisationId(organisationId: string): ProjectModel[] {
    const projectArray: ProjectModel[] = [];
    for (const project of this.projectsList) {
      if (project.organisationId === organisationId) {
        projectArray.push(project);
      }
    }
    return projectArray;
  }

  /**
   *  Ryan Duffy
   *
   *  getFilteredOrganisationProjectsByProjectName
   *
   * : Get a list of projects-list based on the the name and organisationId
   *
   * @param {string} projectName
   * @param {string} organisationId
   * @returns {ProjectModel[]}
   * @memberof ProjectsListService
   */
  getFilteredOrganisationProjectsByProjectName(
    projectName: string,
    organisationId: string
  ): ProjectModel[] {
    const projectArray: ProjectModel[] = [];
    for (const project of this.projectsList) {
      if (
        project.projectName.toLocaleLowerCase().indexOf(projectName.toLocaleLowerCase()) !== -1 &&
        project.organisationId === organisationId
      ) {
        projectArray.push(project);
      }
    }
    return projectArray;
  }

  /**
   *  Ryan Duffy
   *
   *  getOrganisationProjectsByIsClosed
   *
   * : Get a list of Projects based on the on the orgId and whether or not it has been closed
   *
   * @param {boolean} closed
   * @param {string} organisationId
   * @returns {ProjectModel[]}
   * @memberof ProjectsListService
   */
  getOrganisationProjectsByIsClosed(closed: boolean, organisationId: string): ProjectModel[] {
    const projectArray: ProjectModel[] = [];
    for (const project of this.projectsList) {
      if (project.projectClosed === closed && project.organisationId === organisationId) {
        projectArray.push(project);
      }
    }
    return projectArray;
  }
  /**
   *  Warren Dowey
   *
   *  getPublicOrganisationProjects
   *
   * : Get an Project based on the slug by checking the list
   *
   * @param slug of type string
   *
   * @returns a ProjectModel or Null
   */
  getPublicOrganisationProjects(organsiationID: string): ProjectModel[] {
    const publicOrganisationProjects: ProjectModel[] = [];
    for (const project of this.projectsList) {
      if (project.organisationId === organsiationID && project.privateProject !== true) {
        publicOrganisationProjects.push(project);
      }
    }
    return publicOrganisationProjects;
  }

  /**
   *   Warren Dowey & Ryan Duffy
   *
   *  addtoList
   *
   * : handles the insert of a new project-analytics into the list
   *
   * @param data of type ProjectModel
   *
   * @returns Void: inserts a new project-analytics into the lsit
   */
  addToList(data: ProjectModel): void {
    this.projectsList.push(data);
  }
  /**
   *   Warren Dowey & Ryan Duffy
   *
   *  removeFromList
   *
   * : deletes an project-analytics from the list
   *
   * @param data of type ProjectModel
   *
   * @returns Void: deletes a project-analytics
   */
  removeFromList(data: ProjectModel): void {
    let index = 0;
    for (const project of this.projectsList) {
      if (project.projectId === data.projectId) {
        this.projectsList.splice(index, 1);
      }
      index++;
    }
  }
  /**
   *  Warren Dowey
   *
   *  updateInList
   *
   * : handles the update of an project-analytics in the list
   *
   * @param data of type projectmodel
   *
   * @returns Void: update the list with the data
   */
  updateInList(data: ProjectModel): void {
    let index = 0;
    for (let project of this.projectsList) {
      if (project.projectId === data.projectId) {
        this.projectsList.splice(index, 1);
        this.projectsList.push(data);
      }
      index++;
    }
  }
  /**
   *  Warren Dowey
   *
   *  handleData
   *
   * : handles the data from the list by extending the handleData function from the API base service
   *
   * @param data of type ProjectModel
   *
   * @returns Void: get the project-analytics from the postgres database and create the list
   */
  protected handleData(data: any): void {
    this.projectsList = [];
    this.fetchTimestamp = new Date();
    super.handleData(data);
    for (const item of data) {
      const project = new ProjectModel();
      this.addToList(project.fromJson(item));
    }
  }
}
